import { useNavigate, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Car, Truck, Zap, Leaf, Gem, Gauge, Award, Star, MapPin, ArrowRight, Search, HelpCircle,
  ShieldCheck, Wrench, ScanLine, Camera, Route, Building2, Globe, Facebook, ClipboardCheck,
  ChevronDown, Sparkles,
} from 'lucide-react';
import BrandHero from '../components/BrandHero';
import ToolkitPromo from '../components/ToolkitPromo';
import { Seo } from '../components/academy/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const scoreColor = (s) => (s >= 85 ? 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10' : s >= 70 ? 'text-amber-400 border-amber-500/40 bg-amber-500/10' : 'text-orange-400 border-orange-500/40 bg-orange-500/10');

const SAMPLE_REPORT_IMG = 'https://customer-assets.emergentagent.com/job_5a750074-0ac9-4c5b-a0c0-950bd565c9c5/artifacts/sdaxej2l_47BB2B63-4434-420A-B52E-947A20C95406.png';

const GOOGLE_REVIEWS_URL = 'https://share.google/6G8NttFgPUaX5zKRF';
const REVIEWS = [
  { name: 'dexter04', initial: 'D', rating: 5, body: '10 out of 10, will recommend. I was looking to purchase a vehicle and CarLens Auto Inspections took the fear out of buying a used vehicle \u2014 very professional, very thorough, and made the whole process very easy. From now on, anytime I go to buy a car I\u2019ll definitely be using them.' },
  { name: 'Mehdi B.', initial: 'M', rating: 5, body: 'Amazing service that I cannot ever imagine buying another used car without. Saved me thousands of dollars and a potential headache.' },
  { name: 'Evelina G.', initial: 'E', rating: 5, body: 'He did an inspection on a used car I was interested in buying. He was very helpful and explained everything in detail, and was honest too! Would totally recommend him! Great customer service!' },
  { name: 'Steve R.', initial: 'S', rating: 5, body: 'My technician was very professional and provided the most detailed report I\u2019ve ever seen on the condition of the truck I was interested in buying. Answered all of my questions.' },
  { name: 'JT D.', initial: 'J', rating: 5, body: 'One of my players was looking to purchase a car and Daniel was amazing in all of his help. I would absolutely recommend them!' },
  { name: 'Trace D.', initial: 'T', rating: 5, body: 'Had them do an inspection on a vehicle I already own and they were very insightful and helpful \u2014 pointed out things I wouldn\u2019t have caught on my own.' },
];

const CATEGORIES = [
  { Icon: Car, t: 'Cars', d: 'Sedans, coupes, hatchbacks and wagons.' },
  { Icon: Car, t: 'SUVs', d: 'Compact, midsize and full-size SUVs.' },
  { Icon: Truck, t: 'Trucks', d: 'Light-duty and heavy-duty pickup trucks.' },
  { Icon: Leaf, t: 'Hybrids', d: 'Hybrid and plug-in hybrid vehicles, where supported by the CarLens inspection process.' },
  { Icon: Zap, t: 'EVs', d: 'Electric vehicles, where supported by the CarLens inspection process.' },
  { Icon: Gem, t: 'Luxury', d: 'Premium European, Asian and American vehicles.' },
  { Icon: Gauge, t: 'Performance', d: 'Sports cars and performance-oriented vehicles.' },
  { Icon: Sparkles, t: 'Exotic', d: 'Specialty and exotic vehicles, subject to availability and inspection requirements.' },
];

const MAKES = [
  'Acura', 'Alfa Romeo', 'Audi', 'BMW', 'Buick', 'Cadillac', 'Chevrolet', 'Chrysler', 'Dodge',
  'Fiat', 'Ford', 'Genesis', 'GMC', 'Honda', 'Hyundai', 'INFINITI', 'Jaguar', 'Jeep', 'Kia',
  'Land Rover', 'Lexus', 'Lincoln', 'Mazda', 'Mercedes-Benz', 'MINI', 'Mitsubishi', 'Nissan',
  'Porsche', 'Ram', 'Subaru', 'Tesla', 'Toyota', 'Volkswagen', 'Volvo',
];

const MODELS_BY_MAKE = {
  Ford: ['F-150', 'F-250 Super Duty', 'F-350 Super Duty', 'Ranger', 'Maverick', 'Explorer', 'Expedition', 'Escape', 'Bronco', 'Bronco Sport', 'Mustang', 'Edge'],
  Chevrolet: ['Silverado 1500', 'Silverado 2500HD', 'Tahoe', 'Suburban', 'Equinox', 'Traverse', 'Trailblazer', 'Blazer', 'Colorado', 'Malibu', 'Camaro', 'Corvette'],
  GMC: ['Sierra 1500', 'Sierra HD', 'Yukon', 'Yukon XL', 'Acadia', 'Terrain', 'Canyon'],
  Ram: ['1500', '2500', '3500'],
  Toyota: ['Camry', 'Corolla', 'RAV4', 'Highlander', 'Grand Highlander', '4Runner', 'Tacoma', 'Tundra', 'Sequoia', 'Sienna', 'Prius', 'Crown'],
  Honda: ['Accord', 'Civic', 'CR-V', 'HR-V', 'Pilot', 'Passport', 'Ridgeline', 'Odyssey'],
  Nissan: ['Altima', 'Sentra', 'Maxima', 'Rogue', 'Pathfinder', 'Murano', 'Frontier', 'Titan', 'Armada', 'Kicks'],
  Jeep: ['Wrangler', 'Grand Cherokee', 'Cherokee', 'Compass', 'Gladiator', 'Renegade', 'Wagoneer', 'Grand Wagoneer'],
  Hyundai: ['Elantra', 'Sonata', 'Tucson', 'Santa Fe', 'Palisade', 'Kona', 'Santa Cruz', 'Ioniq 5', 'Ioniq 6'],
  Kia: ['Forte', 'K5', 'Soul', 'Seltos', 'Sportage', 'Sorento', 'Telluride', 'Carnival', 'EV6', 'EV9'],
  Subaru: ['Outback', 'Forester', 'Crosstrek', 'Legacy', 'Impreza', 'Ascent', 'WRX', 'BRZ'],
  Mazda: ['Mazda3', 'CX-30', 'CX-5', 'CX-50', 'CX-9', 'CX-90', 'MX-5 Miata'],
  Lexus: ['ES', 'IS', 'LS', 'UX', 'NX', 'RX', 'GX', 'LX'],
  Acura: ['Integra', 'TLX', 'RDX', 'MDX'],
  BMW: ['2 Series', '3 Series', '4 Series', '5 Series', '7 Series', 'X1', 'X3', 'X5', 'X7'],
  'Mercedes-Benz': ['C-Class', 'E-Class', 'S-Class', 'CLA', 'GLA', 'GLB', 'GLC', 'GLE', 'GLS', 'G-Class'],
  Audi: ['A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'Q3', 'Q5', 'Q7', 'Q8'],
  Volkswagen: ['Jetta', 'Golf', 'GTI', 'Tiguan', 'Atlas', 'Taos', 'ID.4'],
  Volvo: ['S60', 'S90', 'XC40', 'XC60', 'XC90'],
  Cadillac: ['CT4', 'CT5', 'XT4', 'XT5', 'XT6', 'Escalade'],
  Lincoln: ['Corsair', 'Nautilus', 'Aviator', 'Navigator'],
  Tesla: ['Model 3', 'Model Y', 'Model S', 'Model X'],
  Porsche: ['Macan', 'Cayenne', 'Panamera', '911', '718 Cayman', '718 Boxster', 'Taycan'],
  'Land Rover': ['Range Rover', 'Range Rover Sport', 'Range Rover Velar', 'Range Rover Evoque', 'Defender', 'Discovery', 'Discovery Sport'],
};

// Future-proofing: map "Make|Model" -> academy guide slug. Empty for now; chips
// automatically become links once guides are added, with no page redesign needed.
const MODEL_GUIDES = {};

const INSPECT_SCOPE = [
  { Icon: Car, t: 'Exterior & Body', d: 'Paint and body condition, visible repairs, glass, lights, trim and exterior concerns.' },
  { Icon: Gauge, t: 'Tires & Wheels', d: 'Tread measurements, tire condition, wear patterns and wheel condition.' },
  { Icon: Wrench, t: 'Engine Bay', d: 'Visible leaks, fluids, belts, hoses, battery condition and general engine-bay observations.' },
  { Icon: ClipboardCheck, t: 'Mechanical', d: 'Engine, transmission, steering, suspension, brakes and other accessible mechanical systems.' },
  { Icon: ShieldCheck, t: 'Undercarriage', d: 'Visible leaks, damage, suspension components, exhaust and structural concerns where accessible.' },
  { Icon: Camera, t: 'Interior', d: 'Seats, controls, instrumentation, electronics, climate controls and interior condition.' },
  { Icon: ScanLine, t: 'OBD-II Diagnostics', d: 'Stored and pending diagnostic information available through the vehicle\u2019s diagnostic system.' },
  { Icon: Route, t: 'Road Test', d: 'Driving characteristics, braking, steering, shifting and noises \u2014 when a road test is permitted and safe.' },
];

const FAQS = [
  { q: 'What types of vehicles does CarLens inspect?', a: 'CarLens inspects most passenger cars, trucks and SUVs \u2014 including sedans, coupes, hatchbacks, wagons, compact to full-size SUVs, pickups, and many hybrids, EVs, luxury and performance vehicles \u2014 throughout Jacksonville and Northeast Florida.' },
  { q: 'Does CarLens inspect pickup trucks?', a: 'Yes. We inspect light-duty and heavy-duty pickups such as the Ford F-150 and Super Duty, Chevrolet Silverado, GMC Sierra, Ram 1500/2500/3500, Toyota Tundra and Tacoma, and more, with attention to the extra wear that towing and hauling create.' },
  { q: 'Does CarLens inspect diesel trucks?', a: 'Yes. We can inspect diesel pickups and read available diesel diagnostic information through the OBD-II system. We focus on condition, leaks, and stored codes rather than teardown or bench testing.' },
  { q: 'Does CarLens inspect luxury vehicles?', a: 'Yes. We inspect premium European, Asian and American vehicles \u2014 brands like BMW, Mercedes-Benz, Audi, Lexus, Porsche, Cadillac, Lincoln, Land Rover and more \u2014 with a careful, buyer-focused evaluation.' },
  { q: 'Does CarLens inspect hybrid vehicles?', a: 'Yes, where supported by our inspection process. We evaluate overall condition, charging behavior where applicable, and available diagnostic information. We do not perform manufacturer-level battery laboratory testing.' },
  { q: 'Does CarLens inspect electric vehicles?', a: 'Yes, where supported by our inspection process. We check overall condition, charging behavior and available diagnostic data. We do not claim specialized high-voltage battery certification or teardown testing.' },
  { q: 'Can CarLens inspect a vehicle at a dealership?', a: 'Yes. When permitted by the dealership, our mobile inspection can be performed right where the vehicle is located, so you don\u2019t have to travel just to evaluate it.' },
  { q: 'Can CarLens inspect a private seller\u2019s vehicle?', a: 'Yes. We coordinate directly with the seller and inspect the vehicle on-site \u2014 one of our most requested services for private-party and Facebook Marketplace purchases.' },
  { q: 'Can you inspect a vehicle I\u2019m buying from out of state?', a: 'Yes. If the vehicle is located within our Northeast Florida service area, we can inspect it and send you a full report so you can decide remotely. See our Out-of-State Car Inspection page for details.' },
  { q: 'What if my vehicle isn\u2019t listed on this page?', a: 'This is not a complete list. CarLens can inspect most passenger cars, trucks and SUVs. Send us the year, make and model and we\u2019ll confirm inspection availability \u2014 no payment required just to ask.' },
  { q: 'Does CarLens inspect classic or exotic vehicles?', a: 'Specialty and exotic vehicles may be inspected subject to availability and inspection requirements. Contact us with the specific vehicle and we\u2019ll confirm whether we can help.' },
  { q: 'Do you inspect motorcycles, RVs or commercial vehicles?', a: 'CarLens focuses on passenger cars, trucks and SUVs. We do not currently offer inspections for motorcycles, RVs/motorhomes, or medium and heavy commercial vehicles. If you\u2019re unsure, just ask and we\u2019ll let you know.' },
];

const SERVICE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Pre-Purchase Vehicle Inspection',
  serviceType: 'Independent pre-purchase inspection for cars, trucks and SUVs',
  description: 'CarLens provides independent pre-purchase vehicle inspections for most cars, trucks and SUVs throughout Jacksonville and Northeast Florida, including a 200+ point inspection, OBD-II diagnostics, 30-50 photos and a Vehicle Decision Report.',
  provider: { '@type': 'AutomotiveBusiness', name: 'CarLens Inspections', url: 'https://carlensauto.com' },
  areaServed: { '@type': 'AdministrativeArea', name: 'Northeast Florida' },
};

const BREADCRUMBS = [
  { name: 'Home', path: '/' },
  { name: 'Vehicles We Inspect', path: '/vehicles-we-inspect' },
];

const ModelChip = ({ make, model }) => {
  const guide = MODEL_GUIDES[`${make}|${model}`];
  if (guide) {
    return (
      <Link to={guide} className="inline-flex items-center rounded-full border border-orange-500/40 bg-orange-500/10 px-3 py-1.5 text-sm text-orange-200 hover:bg-orange-500/20 transition-colors">
        {model}
      </Link>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full border border-zinc-800 bg-[#161618] px-3 py-1.5 text-sm text-zinc-300">
      {model}
    </span>
  );
};

const VehiclesWeInspect = () => {
  const navigate = useNavigate();
  const [recent, setRecent] = useState([]);
  const [query, setQuery] = useState('');
  const [openMake, setOpenMake] = useState(null);

  useEffect(() => {
    axios.get(`${API}/recent-inspections`)
      .then((res) => setRecent((res.data || []).slice(0, 3)))
      .catch(() => setRecent([]));
  }, []);

  const q = query.trim().toLowerCase();

  const filteredMakes = useMemo(
    () => (q ? MAKES.filter((m) => m.toLowerCase().includes(q)) : MAKES),
    [q]
  );

  const directory = useMemo(() => {
    const makes = Object.keys(MODELS_BY_MAKE);
    if (!q) return makes.map((mk) => ({ make: mk, models: MODELS_BY_MAKE[mk] }));
    const out = [];
    makes.forEach((mk) => {
      const makeHit = mk.toLowerCase().includes(q);
      const models = makeHit ? MODELS_BY_MAKE[mk] : MODELS_BY_MAKE[mk].filter((md) => md.toLowerCase().includes(q));
      if (makeHit || models.length) out.push({ make: mk, models });
    });
    return out;
  }, [q]);

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }} data-testid="vehicles-we-inspect-page">
      <Seo
        siteName="CarLens"
        title="Vehicles We Inspect | Cars, Trucks & SUVs"
        description="CarLens provides independent pre-purchase inspections for most cars, trucks and SUVs throughout Jacksonville and Northeast Florida. Explore the makes and vehicles we inspect."
        path="/vehicles-we-inspect"
        faq={FAQS}
        breadcrumbs={BREADCRUMBS}
        extraSchemas={[SERVICE_SCHEMA]}
      />

      <BrandHero />

      {/* HERO */}
      <section className="max-w-5xl mx-auto px-6 pt-10 pb-14 md:pt-14 text-center" data-testid="vwi-hero">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.06]" style={{ fontFamily: 'Space Grotesk' }}>
          Vehicles We Inspect
        </h1>
        <p className="mt-6 text-2xl md:text-3xl font-semibold text-orange-400" style={{ fontFamily: 'Space Grotesk' }}>
          Nearly Every Make. Nearly Every Model. One Independent Inspection.
        </p>
        <p className="mt-5 text-base md:text-lg text-zinc-300 leading-relaxed max-w-3xl mx-auto">
          Whether you&rsquo;re buying an everyday commuter, family SUV, pickup truck, luxury vehicle or performance car, CarLens provides independent pre-purchase vehicle inspections designed to help you know what you&rsquo;re buying before you make the decision.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 mt-8 justify-center">
          <Button size="lg" className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg shadow-orange-500/20" onClick={() => navigate('/book')} data-testid="vwi-hero-book-btn">
            Book an Inspection
          </Button>
          <Button size="lg" variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-white/5 px-8 py-6 text-lg rounded-lg" onClick={() => navigate('/what-you-receive')} data-testid="vwi-hero-sample-btn">
            View Sample Report
          </Button>
        </div>
        <p className="mt-6 text-sm text-zinc-400 tracking-wide">
          200+ Inspection Points • OBD-II Diagnostics • 30&ndash;50 Photos • Vehicle Decision Report&trade; • Independent, Buyer-Focused Inspection
        </p>
      </section>

      {/* SECTION 1 — categories */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="vwi-categories">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12" style={{ fontFamily: 'Space Grotesk' }}>From Daily Drivers to Dream Cars</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {CATEGORIES.map((c) => (
            <div key={c.t} className="rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 p-6 hover:border-orange-500/50 transition-colors" data-testid={`vwi-cat-${c.t.toLowerCase()}`}>
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-4">
                <c.Icon className="w-6 h-6 text-orange-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-1.5">{c.t}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SEARCH */}
      <section className="max-w-3xl mx-auto px-6 pt-6 pb-2" data-testid="vwi-search">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your vehicle…"
            className="bg-[#161618] border-zinc-800 text-white h-14 pl-12 text-base rounded-xl focus-visible:ring-orange-500"
            data-testid="vwi-search-input"
          />
        </div>
      </section>

      {/* SECTION 2 — makes */}
      <section className="max-w-7xl mx-auto px-6 py-10" data-testid="vwi-makes">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>Popular Makes We Inspect</h2>
          <p className="text-zinc-400">CarLens inspects most major vehicle manufacturers.</p>
        </div>
        {filteredMakes.length === 0 ? (
          <p className="text-center text-zinc-500">No makes match &ldquo;{query}&rdquo;.</p>
        ) : (
          <div className="flex flex-wrap justify-center gap-3">
            {filteredMakes.map((m) => (
              <span key={m} className="inline-flex items-center rounded-full border border-zinc-800 bg-[#161618] px-4 py-2 text-sm text-zinc-200 hover:border-orange-500/50 hover:text-white transition-colors" data-testid={`vwi-make-${m.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                {m}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* SECTION 3 — model directory */}
      <section className="max-w-5xl mx-auto px-6 py-10" data-testid="vwi-models">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-8" style={{ fontFamily: 'Space Grotesk' }}>Popular Vehicles We Inspect</h2>
        {directory.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-zinc-400 mb-4">We didn&rsquo;t find &ldquo;{query}&rdquo; in the list \u2014 but this isn&rsquo;t everything we inspect.</p>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={() => navigate('/contact')} data-testid="vwi-models-ask-btn">Ask About My Vehicle</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {directory.map(({ make, models }) => {
              const isOpen = q ? true : openMake === make;
              return (
                <div key={make} className="rounded-2xl border border-zinc-800 bg-[#151517] overflow-hidden" data-testid={`vwi-make-block-${make.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                  <button
                    className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-white/[0.02] transition-colors"
                    onClick={() => setOpenMake(isOpen && !q ? null : make)}
                    data-testid={`vwi-make-toggle-${make.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  >
                    <span className="text-lg font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>{make}</span>
                    <span className="flex items-center gap-3">
                      <span className="text-xs text-zinc-500">{models.length} models</span>
                      <ChevronDown className={`w-5 h-5 text-orange-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-5 pt-1 flex flex-wrap gap-2.5">
                      {models.map((md) => <ModelChip key={md} make={make} model={md} />)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* SECTION 4 — don't see your vehicle */}
      <section className="max-w-3xl mx-auto px-6 py-14" data-testid="vwi-ask">
        <div className="rounded-3xl border border-orange-500/30 bg-gradient-to-r from-orange-500/10 to-orange-600/10 p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>Don&rsquo;t See Your Vehicle?</h2>
          <p className="text-zinc-300 leading-relaxed mb-7 max-w-xl mx-auto">
            This isn&rsquo;t a complete list. CarLens can inspect most passenger cars, trucks and SUVs. If you don&rsquo;t see the vehicle you&rsquo;re considering, send us the year, make and model and we&rsquo;ll confirm inspection availability.
          </p>
          <Button size="lg" className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg" onClick={() => navigate('/contact')} data-testid="vwi-ask-btn">
            Ask About My Vehicle
          </Button>
        </div>
      </section>

      {/* SECTION 5 — inspection scope */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="vwi-scope">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12" style={{ fontFamily: 'Space Grotesk' }}>What Does CarLens Inspect?</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {INSPECT_SCOPE.map((s) => (
            <div key={s.t} className="rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 p-6 hover:border-orange-500/50 transition-colors" data-testid={`vwi-scope-${s.t.split(' ')[0].toLowerCase()}`}>
              <div className="w-11 h-11 rounded-xl bg-orange-500/10 flex items-center justify-center mb-4">
                <s.Icon className="w-5 h-5 text-orange-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-1.5">{s.t}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 6 — decide, not just inspect */}
      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="vwi-decide">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative mx-auto w-full max-w-md order-2 lg:order-1">
            <div className="absolute -inset-4 bg-gradient-to-r from-orange-500/25 to-zinc-600/20 rounded-3xl blur-3xl" />
            <img src={SAMPLE_REPORT_IMG} alt="Sample CarLens Vehicle Decision Report with Confidence Score" className="relative rounded-2xl shadow-2xl border-2 border-zinc-700/60 w-full h-auto cursor-pointer hover:scale-[1.02] transition-transform duration-300" onClick={() => navigate('/what-you-receive')} loading="lazy" />
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-5 leading-tight" style={{ fontFamily: 'Space Grotesk' }}>We Don&rsquo;t Just Inspect the Car. We Help You Decide.</h2>
            <p className="text-zinc-400 leading-relaxed mb-5">
              A traditional inspection hands you a checklist. CarLens goes further: every finding is organized into the CarLens Vehicle Decision Report&trade; so you can actually understand what it means for your purchase.
            </p>
            <ul className="space-y-2.5 mb-8">
              {['What looks good', 'What needs attention', 'Potential red flags', 'Upcoming ownership considerations', 'Diagnostic findings', 'CarLens Confidence Score\u2122', 'Buy / Negotiate / Walk Away guidance'].map((li) => (
                <li key={li} className="flex items-center gap-3 text-zinc-300"><ShieldCheck className="w-4 h-4 text-orange-400 shrink-0" /> {li}</li>
              ))}
            </ul>
            <Button size="lg" className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg" onClick={() => navigate('/what-you-receive')} data-testid="vwi-decide-sample-btn">
              View Sample Report
            </Button>
          </div>
        </div>
      </section>

      {/* SECTION 7 — recent inspections */}
      {recent.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-16" data-testid="vwi-recent">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>Real Cars. Real Inspections. Real Findings.</h2>
            <p className="text-zinc-400 text-base md:text-lg max-w-2xl mx-auto">Actual vehicles our inspectors recently evaluated \u2014 each with a CarLens Confidence Score\u2122.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recent.map((it) => (
              <Link key={it.id} to="/recent-inspections" className="group rounded-2xl border border-zinc-800 bg-[#151517] overflow-hidden hover:border-orange-500/50 transition-colors" data-testid={`vwi-recent-card-${it.id}`}>
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={(it.photos || [])[0]} alt={`${it.vehicle} inspected by CarLens`} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <span className={`absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-full border ${scoreColor(it.score)}`}>{it.score} Score</span>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-white leading-tight group-hover:text-orange-300 transition-colors" style={{ fontFamily: 'Space Grotesk' }}>{it.vehicle}</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">{it.when}</p>
                  <p className="mt-3 text-sm text-zinc-400 leading-6 line-clamp-3">{it.summary}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/recent-inspections" className="inline-flex items-center gap-2 rounded-full border border-orange-500/50 text-orange-300 hover:bg-orange-500/10 px-7 py-3 font-semibold transition-colors" data-testid="vwi-see-recent-btn">
              See Recent Inspections <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {/* SECTION 8 & 9 — out-of-state + dealership */}
      <section className="max-w-7xl mx-auto px-6 py-12" data-testid="vwi-crosslinks">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 p-8" data-testid="vwi-oos">
            <Globe className="w-8 h-8 text-orange-400 mb-4" />
            <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>Buying From Out of State?</h2>
            <p className="text-zinc-400 leading-relaxed mb-6">Buying a vehicle located in Northeast Florida while you live somewhere else? CarLens can inspect the vehicle at the dealership or seller&rsquo;s location and give you the information you need remotely.</p>
            <Link to="/out-of-state-car-inspection" className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-semibold" data-testid="vwi-oos-link">Out-of-State Buyers <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 p-8" data-testid="vwi-dealer">
            <Building2 className="w-8 h-8 text-orange-400 mb-4" />
            <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>Going to a Dealership?</h2>
            <p className="text-zinc-400 leading-relaxed mb-6">The vehicle doesn&rsquo;t necessarily have to leave the dealer for CarLens to inspect it. When permitted by the dealership, our mobile inspection can be performed right where the vehicle is located.</p>
            <Link to="/services/dealer-lot-inspection" className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-semibold" data-testid="vwi-dealer-link">Dealer Lot Inspections <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* SECTION 10 — reviews */}
      <section className="py-24 bg-gradient-to-b from-white to-zinc-100" data-testid="vwi-reviews">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-zinc-900 mb-4" style={{ fontFamily: 'Space Grotesk' }}>What Our Customers Say</h2>
            <div className="inline-flex items-center gap-3 rounded-full border border-zinc-200 bg-white px-5 py-2.5 shadow-sm">
              <span className="text-2xl font-extrabold text-zinc-900" style={{ fontFamily: 'Space Grotesk' }}>5.0</span>
              <span className="flex items-center gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-orange-400 text-orange-400" />)}</span>
              <span className="text-zinc-500 text-sm"><span className="font-semibold text-zinc-700">5-star reviews</span></span>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {REVIEWS.map((r) => (
              <div key={r.name} className="bg-white rounded-2xl p-6 shadow-xl flex flex-col" data-testid={`vwi-review-${r.initial}`}>
                <div className="flex items-center gap-1 mb-4">{[...Array(r.rating)].map((_, i) => <Star key={i} className="w-5 h-5 fill-orange-400 text-orange-400" />)}</div>
                <p className="text-zinc-700 mb-6 leading-relaxed flex-1">"{r.body}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold text-lg shrink-0" style={{ fontFamily: 'Space Grotesk' }}>{r.initial}</div>
                  <div>
                    <div className="font-semibold text-zinc-900">{r.name}</div>
                    <div className="flex items-center gap-1.5 text-sm text-zinc-500">
                      <img src="https://www.gstatic.com/images/branding/googleg/1x/googleg_standard_color_18dp.png" alt="Google" className="w-4 h-4" loading="lazy" />
                      Google review
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-zinc-900 text-white px-7 py-3.5 font-semibold hover:bg-zinc-800 transition-colors" data-testid="vwi-google-reviews-btn">
              <img src="https://www.gstatic.com/images/branding/googleg/1x/googleg_standard_color_18dp.png" alt="" className="w-5 h-5 bg-white rounded-full p-0.5" />
              Read all reviews on Google
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 11 — FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-20" data-testid="vwi-faq">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-3">
            <HelpCircle className="w-6 h-6 text-orange-500" />
            <span className="text-orange-400 font-semibold tracking-wide uppercase text-sm">FAQ</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Vehicle Inspection Questions</h2>
        </div>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <details key={i} className="group rounded-2xl border border-zinc-800 bg-[#161618] px-6 py-5 hover:border-orange-500/40 transition-colors" data-testid={`vwi-faq-${i}`}>
              <summary className="flex items-center justify-between cursor-pointer list-none text-white font-semibold text-base md:text-lg">
                <span>{f.q}</span>
                <ArrowRight className="w-5 h-5 text-orange-400 flex-shrink-0 ml-4 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-4 text-zinc-300 text-base leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
        <div className="text-center mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2">
          <Link to="/services/pre-purchase-inspection" className="text-orange-400 hover:text-orange-300 font-medium">Pre-Purchase Inspection</Link>
          <Link to="/services/facebook-marketplace-inspection" className="text-orange-400 hover:text-orange-300 font-medium">Facebook Marketplace</Link>
          <Link to="/services/private-seller-inspection" className="text-orange-400 hover:text-orange-300 font-medium">Private Seller</Link>
          <Link to="/locations/jacksonville" className="text-orange-400 hover:text-orange-300 font-medium">Jacksonville</Link>
          <Link to="/academy" className="text-orange-400 hover:text-orange-300 font-medium">CarLens Academy</Link>
        </div>
      </section>

      {/* Toolkit promotion */}
      <div className="max-w-7xl mx-auto px-6">
        <ToolkitPromo variant="compact" />
      </div>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="vwi-final-cta">
        <div className="bg-gradient-to-r from-orange-600/20 to-orange-500/20 rounded-3xl border border-orange-500/30 p-10 md:p-14 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: 'Space Grotesk' }}>
            Found the Car? Now Find Out the Truth.
          </h2>
          <p className="text-zinc-300 text-lg mb-8 max-w-2xl mx-auto">Before you buy, let CarLens take a closer look.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg" onClick={() => navigate('/book')} data-testid="vwi-final-book-btn">
              Book a CarLens Inspection
            </Button>
            <Button size="lg" variant="outline" className="border-orange-500/50 text-orange-300 hover:bg-orange-500/10 px-8 py-6 text-lg rounded-lg" onClick={() => navigate('/firstlook')} data-testid="vwi-final-firstlook-btn">
              CarLens FirstLook
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 mt-8">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-white font-semibold tracking-wide" style={{ fontFamily: 'Space Grotesk' }}>CarLens Automotive Intelligence</p>
              <p className="text-zinc-500 text-sm">© 2025 CarLens Automotive Intelligence. All rights reserved.</p>
            </div>
            <a href="https://www.facebook.com/profile.php?id=61583275422342" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-zinc-400 hover:text-zinc-600 transition-colors">
              <Facebook className="w-5 h-5" />
              <span>Follow us on Facebook</span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VehiclesWeInspect;

